import React, { useState } from 'react';

// --- Data for the FAQs ---
const faqData = [
  {
    question: "How do I design my own T-shirt?",
    answer: "You can use our online design tool to upload your artwork, add text, and customize your T-shirt. If you need help, our design team is available to assist you."
  },
  {
    question: "What printing methods do you use?",
    answer: "We use advanced printing techniques including screen printing, direct-to-garment (DTG), and heat transfer, ensuring your designs are vibrant, detailed, and long-lasting.",
    defaultOpen: true 
  },
  {
    question: "Can I place bulk or corporate orders?",
    answer: "Absolutely! We offer discounts for bulk and corporate orders. Please contact our sales team for a custom quote."
  },
  {
    question: "How long will it take to receive my order?",
    answer: "Standard shipping typically takes 5-7 business days. Expedited shipping options are available at checkout."
  },
  {
    question: "What if I'm not satisfied with my order?",
    answer: "Your satisfaction is our priority. If you're not happy with your order, please contact our customer support within 30 days for a return or replacement."
  },
  {
    question: "Do you ship outside California?",
    answer: "Yes! While we're proudly based in California, we offer nationwide shipping across the U.S. and international delivery options for select countries.",
    defaultOpen: true
  },
  {
    question: "Can I use my own logo or artwork?",
    answer: "Yes, you can upload your own logo, artwork, or photos. Please ensure they meet our file format and resolution guidelines for the best print quality."
  },
  {
    question: "What file formats do you accept for designs?",
    answer: "We accept a variety of file formats, including PNG, JPEG, PDF, AI, and PSD. For best results, we recommend high-resolution PNG or vector files."
  },
  {
    question: "Do you offer design assistance?",
    answer: "We do! If you have an idea but need help bringing it to life, our team of professional designers can assist you with your artwork."
  },
  {
    question: "Can I print on both sides of the T-shirt?",
    answer: "Yes, you can choose to print on the front, back, or both sides of the T-shirt during the design process."
  }
];

// --- Individual FAQ Item Component ---
function FaqItem({ question, answer, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center w-full p-4 md:p-5 text-left hover:bg-gray-50 rounded-t-lg transition-colors duration-200"
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-sm md:text-base text-gray-900 pr-4">
          {question}
        </span>
        <span className={`text-xl md:text-2xl font-light flex-shrink-0 transition-transform duration-300 ${
          isOpen ? 'rotate-180 text-[#6cc5bf]' : 'text-gray-500'
        }`}>
          {isOpen ? '−' : '+'}
        </span>
      </button>
      
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-4 md:p-5 pt-0 text-gray-600 text-sm md:text-base leading-relaxed">
          {answer}
        </div>
      </div>
    </div>
  );
}

// --- Main FAQs Component ---
function Faqs() {
  const middleIndex = Math.ceil(faqData.length / 2);
  const firstColumn = faqData.slice(0, middleIndex);
  const secondColumn = faqData.slice(middleIndex);

  return (
    <div className="bg-white py-12 md:py-16 lg:py-24 font-custom">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <p className="text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
            FAQs
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900">
            <span className="bg-[#e6f7f5] px-3 py-1 rounded">
              Frequently Ask Questions
            </span>
          </h2>
        </div>
        
        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
          
          {/* First Column */}
          <div className="flex flex-col gap-4">
            {firstColumn.map((faq, index) => (
              <FaqItem 
                key={index} 
                question={faq.question} 
                answer={faq.answer}
                defaultOpen={faq.defaultOpen}
              />
            ))}
          </div>
          
          {/* Second Column */}
          <div className="flex flex-col gap-4">
            {secondColumn.map((faq, index) => (
              <FaqItem 
                key={index + middleIndex} 
                question={faq.question} 
                answer={faq.answer}
                defaultOpen={faq.defaultOpen}
              />
            ))}
          </div>
        </div>
        
      </div>
    </div>
  );
}

export default Faqs;